import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Grid,
  Container,
  Card,
  CardContent,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  useScrollTrigger,
  Slide,
  Fade,
  Zoom,
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import {
  ArrowForward as ArrowForwardIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as CarsIcon,
  People as PeopleIcon,
  AccessTime as ScheduleIcon,
  Route as RouteIcon,
  Analytics as AnalyticsIcon,
  Create as TemplateIcon,
  Phone as PhoneIcon,
  Close as CloseIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Devices as DevicesIcon,
  Smartphone as SmartphoneIcon,
  Gavel as GavelIcon,
  PrivacyTip as PrivacyTipIcon,
} from '@mui/icons-material';
import { loginUser } from '../../redux/slices/authSlice';
import brandingConfig from '../../config/brandingConfig';
import { PrimaryLogo, IconLogo } from '../common/BrandedImage';

// Header with transparent background that becomes solid on scroll
const HideOnScroll = (props) => {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  transition: 'all 0.5s ease',
  height: '64px',
  '&.scrolled': {
    background: alpha(theme.palette.primary.main, 0.95),
    boxShadow: theme.shadows[4],
  },
}));

// Hero Section
const HeroSection = styled(Box)(({ theme }) => ({
  background: `
    linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(30, 30, 30, 0.75) 50%, rgba(0, 0, 0, 0.85) 100%),
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 35px,
      rgba(255, 204, 0, 0.03) 35px,
      rgba(255, 204, 0, 0.03) 70px
    ),
    linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)
  `,
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.common.white,
  textAlign: 'center',
  position: 'relative',
  padding: theme.spacing(4),
}));

// Feature Card
const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  minHeight: 220,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[6],
  },
}));

// Login Form Container
const LoginContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '400px',
  width: '100%',
  borderRadius: theme.shape.borderRadius * 2,
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  boxShadow: theme.shadows[5],
}));

const Form = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(1),
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(3, 0, 2),
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1),
}));

// Footer links style
const FooterLink = styled(Link)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(1),
  color: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const LandingPage = ({ directLogin = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { isLoading, error } = useSelector((state) => state.auth);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Automatisches Scrollen zum Login-Bereich für White-Label-Versionen
  const loginSectionRef = useRef(null);
  
  // Bestimme, ob es sich um die Default-App (NextCab) handelt
  const isDefaultApp = brandingConfig.appName === "NextCab";
  
  // Handle scroll behavior on page load
  // Removed for now - we'll address the scroll position issue later

  // Handle scroll event to change header appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Scroll-Logik basierend auf App-Typ
  useEffect(() => {
    // Bei der Standard-App (NextCab): Immer zum Anfang der Seite scrollen
    if (!directLogin && isDefaultApp) {
      // Sicherstellen, dass die Seite ganz oben beginnt
      window.scrollTo(0, 0);
    } 
    // Bei White-Label-Apps: Direkt zum Login-Bereich scrollen
    else if (directLogin && loginSectionRef.current) {
      loginSectionRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [directLogin, isDefaultApp]);
  
  const validateEmail = (email) => {
    if (!email) {
      setEmailError('E-Mail ist erforderlich');
      return false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      setEmailError('Ungültige E-Mail-Adresse');
      return false;
    }
    setEmailError('');
    return true;
  };
  
  const validatePassword = (password) => {
    if (!password) {
      setPasswordError('Passwort ist erforderlich');
      return false;
    } else if (password.length < 8) {
      setPasswordError('Passwort muss mindestens 8 Zeichen lang sein');
      return false;
    }
    setPasswordError('');
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    
    if (isEmailValid && isPasswordValid) {
      try {
        const resultAction = await dispatch(loginUser({ email, password }));
        if (loginUser.fulfilled.match(resultAction)) {
          if (location.state?.returnUrl) {
            navigate(location.state.returnUrl);
          } else {
            navigate('/dashboard');
          }
        }
      } catch (error) {
        console.error('Login failed:', error);
      }
    }
  };

  const handleToggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  // Zentrale Funktion zum Scrollen zum Login-Bereich
  const scrollToLogin = () => {
    // Bei White-Label wird direkt das Element mit der ID "login-section" verwendet,
    // bei der Standard-App das Element mit ID "client-login"
    const loginElementId = isDefaultApp ? "client-login" : "login-section";
    smoothScrollToElement(loginElementId);
  };
  
  // Funktion für weiches Scrollen zu Elementen über die Navbar
  const smoothScrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Navbar items
  const navItems = [
    { text: 'Startseite', href: '#home' },
    { text: 'Features', href: '#features' },
    { text: 'Mobile App', href: '#mobile-app' },
    { text: 'White Label', href: '#white-label' },
    { text: 'Kontakt', href: '#contact' },
  ];

  // Feature items
  const features = [
    {
      icon: <DashboardIcon fontSize="large" color="primary" />,
      title: "Übersichtliches Dashboard",
      description: "Behalten Sie den Überblick über alle laufenden Aktivitäten Ihres Taxiunternehmens auf einen Blick."
    },
    {
      icon: <CarsIcon fontSize="large" color="primary" />,
      title: "Fahrzeugverwaltung",
      description: "Verwalten Sie Ihre Fahrzeugflotte mit detaillierten Informationen zu jedem Fahrzeug."
    },
    {
      icon: <PeopleIcon fontSize="large" color="primary" />,
      title: "Fahrerverwaltung",
      description: "Organisieren Sie Ihre Fahrer, deren Profile, Dokumente und Verfügbarkeiten."
    },
    {
      icon: <RouteIcon fontSize="large" color="primary" />,
      title: "Fahrtverwaltung",
      description: "Erfassen, planen und verfolgen Sie alle Fahrten inklusive Vorbestellungen und wiederkehrende Fahrten."
    },
    {
      icon: <ScheduleIcon fontSize="large" color="primary" />,
      title: "Schichtplanung",
      description: "Erstellen Sie effiziente Schichtpläne für Ihre Fahrer mit Kalenderansicht."
    },
    {
      icon: <AnalyticsIcon fontSize="large" color="primary" />,
      title: "Berichte & Analysen",
      description: "Generieren Sie umfassende Berichte für fundierte Geschäftsentscheidungen."
    }
  ];

  // Mobile App Features
  const mobileFeatures = [
    {
      icon: <LocationIcon fontSize="large" />,
      title: "Live-Tracking",
      description: "Kunden können Ihre Fahrer in Echtzeit verfolgen und erhalten genaue Ankunftszeiten."
    },
    {
      icon: <EventIcon fontSize="large" />,
      title: "Vorbestellungen",
      description: "Einfache Planung von Fahrten im Voraus mit Kalenderintegration und Erinnerungen."
    },
    {
      icon: <SmartphoneIcon fontSize="large" />,
      title: "Benutzerfreundliche App",
      description: "Intuitive Benutzeroberfläche für Kunden und Fahrer auf iOS und Android."
    },
    {
      icon: <SpeedIcon fontSize="large" />,
      title: "Schnelle Bestellung",
      description: "Taxi mit wenigen Klicks bestellen oder Favoriten für regelmäßige Strecken speichern."
    }
  ];

  return (
    <>
      {/* Navigation - nur für NextCab anzeigen oder wenn directLogin=false */}
      {!directLogin && (
        <HideOnScroll>
          <StyledAppBar position="fixed" className={scrolled ? 'scrolled' : ''}>
            <Toolbar sx={{ height: '64px', overflow: 'hidden' }}>
              <Box display="flex" alignItems="center" flexGrow={1} sx={{ maxHeight: '64px', overflow: 'hidden' }}>
                <IconLogo
                  alt={`${brandingConfig.appName} Logo`}
                  style={{ height: 44, marginRight: 0 }}
                />
                <PrimaryLogo
                  alt={brandingConfig.appName}
                  style={{ height: 130, marginLeft: -4 }}
                />
              </Box>
              
              {/* Navigations-Tabs nur für Default NextCab App anzeigen */}
              {isDefaultApp && !isMobile && (
                <Box display="flex">
                  {navItems.map((item) => (
                    <Button
                      key={item.text}
                      color={scrolled ? "inherit" : "inherit"}
                      onClick={() => smoothScrollToElement(item.href.substring(1))}
                      sx={{ mx: 1 }}
                    >
                      {item.text}
                    </Button>
                  ))}
                  <Button 
                    variant="contained" 
                    color="secondary"
                    onClick={scrollToLogin}
                    sx={{ ml: 2 }}
                  >
                    Anmelden
                  </Button>
                </Box>
              )}
              
              {/* Bei White-Label-Versionen nur den Login-Button anzeigen */}
              {!isDefaultApp && !isMobile && (
                <Box display="flex">
                  <Button 
                    variant="contained" 
                    color="secondary"
                    onClick={scrollToLogin}
                    sx={{ ml: 2 }}
                  >
                    Anmelden
                  </Button>
                </Box>
              )}
              
              {isMobile && (
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleToggleDrawer}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </StyledAppBar>
        </HideOnScroll>
      )}
      
      {/* Mobile Drawer - nur anzeigen, wenn Navigation sichtbar ist */}
      {!directLogin && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleToggleDrawer}
        >
          <Box sx={{ width: 250 }} role="presentation">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconLogo
                  alt={`${brandingConfig.appName} Logo`}
                  style={{ height: 30, marginRight: 0 }}
                />
                <PrimaryLogo
                  alt={brandingConfig.appName}
                  style={{ height: 70, marginLeft: -2 }}
                />
              </Box>
              <IconButton onClick={handleToggleDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <List>
              {/* Navigation Items nur für Default App (NextCab) anzeigen */}
              {isDefaultApp && navItems.map((item) => (
                <ListItem 
                  button 
                  key={item.text} 
                  onClick={() => {
                    smoothScrollToElement(item.href.substring(1));
                    handleToggleDrawer();
                  }}
                >
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
              <ListItem 
                button 
                onClick={() => {
                  scrollToLogin();
                  handleToggleDrawer();
                }}
              >
                <ListItemText primary="Anmelden" />
              </ListItem>

              {/* Rechtliche Seiten im Menü */}
              <Divider sx={{ my: 1 }} />
              <ListItem 
                button 
                component={RouterLink}
                to="/datenschutz"
                onClick={handleToggleDrawer}
              >
                <ListItemText primary="Datenschutz" />
              </ListItem>
              <ListItem 
                button 
                component={RouterLink}
                to="/impressum"
                onClick={handleToggleDrawer}
              >
                <ListItemText primary="Impressum" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      )}
      
      {/* Hero Section - nur für NextCab und wenn nicht directLogin */}
      {(!directLogin && isDefaultApp) && (
        <HeroSection id={isDefaultApp ? "home" : "landing-home"}>
          <Fade in={true} timeout={1000}>
            <Box>
              <Typography variant="h1" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                {brandingConfig.appName}
              </Typography>
              <Typography variant="h4" component="h2" sx={{ mb: 4 }}>
                Die komplette Lösung für Ihr Taxi-Unternehmen
              </Typography>
              <Box sx={{ maxWidth: 700, mx: 'auto', mb: 6 }}>
                <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                  Verwaltung von Fahrern, Fahrzeugen und Fahrten in einer übersichtlichen Web-App.
                  Kombiniert mit einer leistungsstarken mobilen App für Ihre Kunden und Fahrer.
                </Typography>
              </Box>
              <Grid container spacing={2} justifyContent="center">
                <Grid item>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    onClick={() => smoothScrollToElement('features')}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Features entdecken
                  </Button>
                </Grid>
                <Grid item>
                  <Button 
                    variant="outlined" 
                    color="inherit" 
                    size="large"
                    onClick={scrollToLogin}
                    sx={{ px: 4, py: 1.5, borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: alpha('#fff', 0.1) } }}
                  >
                    Anmelden
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </HeroSection>
      )}
      
      {/* Für White-Label Apps (nicht NextCab) und directLogin=false: Vereinfachter Header */}
      {(!directLogin && !isDefaultApp) && (
        <HeroSection id="home" sx={{ minHeight: '50vh' }}>
          <Fade in={true} timeout={1000}>
            <Box>
              <Typography variant="h1" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                {brandingConfig.appName}
              </Typography>
              <Typography variant="h4" component="h2" sx={{ mb: 4 }}>
                Admin Portal
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                onClick={scrollToLogin}
                sx={{ px: 4, py: 1.5 }}
              >
                Zum Login
              </Button>
            </Box>
          </Fade>
        </HeroSection>
      )}
      
      {/* Features Section - nur für Default App (NextCab) und nicht directLogin */}
      {(!directLogin && isDefaultApp) && (
        <Box id="features" sx={{ py: 10, px: 3, backgroundColor: theme.palette.background.default }}>
          <Container maxWidth="lg">
            <Typography variant="h2" component="h2" align="center" fontWeight="bold" sx={{ mb: 2 }}>
              Hauptfunktionen
            </Typography>
            <Typography variant="h5" component="p" align="center" color="textSecondary" sx={{ mb: 8 }}>
              Alles was Sie für die effiziente Verwaltung Ihres Taxi-Unternehmens benötigen
            </Typography>
            
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Zoom in={true} style={{ transitionDelay: `${100 * index}ms` }}>
                    <FeatureCard>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                          <Box sx={{ mb: 2, height: 60, display: 'flex', alignItems: 'center' }}>
                            {feature.icon}
                          </Box>
                          <Typography variant="h5" component="h3" gutterBottom fontWeight={500}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body1" color="textSecondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </CardContent>
                    </FeatureCard>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
      
      {/* Live Tracking Demo - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box sx={{ position: 'relative', overflow: 'hidden', py: 10, bgcolor: theme.palette.grey[100] }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={5}>
                <Fade in={true} timeout={1000}>
                  <Box>
                    <Typography variant="h3" component="h2" gutterBottom>
                      Echtzeit-Tracking und Verwaltung
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Behalten Sie den Überblick über alle Ihre Fahrer in Echtzeit. Sehen Sie deren Position, Status und aktuelle Fahrten auf einer interaktiven Karte.
                    </Typography>
                    <Typography variant="body1" paragraph>
                      Die leistungsstarke Verwaltungsoberfläche ermöglicht Ihnen die effiziente Planung von Vorbestellungen und die optimale Zuweisung von Fahrern.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      onClick={scrollToLogin}
                    >
                      Jetzt testen
                    </Button>
                  </Box>
                </Fade>
              </Grid>
              <Grid item xs={12} md={7}>
                {/* CSS Monitor Mockup */}
                <Box
                  sx={{
                    transform: 'perspective(1000px) rotateY(-10deg) rotateX(5deg)',
                    transition: 'transform 0.5s ease',
                    '&:hover': {
                      transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                    },
                  }}
                >
                  {/* Monitor Frame */}
                  <Box sx={{
                    border: '3px solid #1a1a1a',
                    borderBottom: 'none',
                    borderRadius: '12px 12px 0 0',
                    bgcolor: '#1a1a1a',
                    overflow: 'hidden',
                    boxShadow: 8,
                  }}>
                    {/* Browser Chrome */}
                    <Box sx={{
                      height: 32,
                      bgcolor: '#2d2d2d',
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      gap: 0.8,
                    }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#febc2e' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
                      <Box sx={{
                        flex: 1,
                        height: 20,
                        bgcolor: '#3a3a3a',
                        borderRadius: 1,
                        ml: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                      }}>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.6rem' }}>
                          app.nextcab.de/dashboard
                        </Typography>
                      </Box>
                    </Box>
                    {/* App Content */}
                    <Box sx={{ display: 'flex', height: 320 }}>
                      {/* Sidebar */}
                      <Box sx={{
                        width: 56,
                        bgcolor: theme.palette.secondary.main,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 1.5,
                        gap: 1,
                      }}>
                        <Box sx={{ width: 28, height: 28, mb: 1 }}>
                          <IconLogo
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                        {[DashboardIcon, CarsIcon, PeopleIcon, RouteIcon, ScheduleIcon, AnalyticsIcon].map((Icon, i) => (
                          <Box key={i} sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: i === 0 ? alpha(theme.palette.primary.main, 0.2) : 'transparent',
                          }}>
                            <Icon sx={{ fontSize: 16, color: i === 0 ? theme.palette.primary.main : '#888' }} />
                          </Box>
                        ))}
                      </Box>
                      {/* Main Area */}
                      <Box sx={{ flex: 1, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
                        {/* Top Bar */}
                        <Box sx={{
                          height: 40,
                          bgcolor: '#fff',
                          borderBottom: '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#333' }}>Dashboard</Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ width: 50, height: 8, bgcolor: '#eee', borderRadius: 1 }} />
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: theme.palette.primary.main, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <PeopleIcon sx={{ fontSize: 12, color: '#000' }} />
                            </Box>
                          </Box>
                        </Box>
                        {/* Dashboard Content */}
                        <Box sx={{ flex: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {/* KPI Row */}
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {[
                              { label: 'Fahrer aktiv', value: '12', color: theme.palette.success.main },
                              { label: 'Fahrten heute', value: '47', color: theme.palette.primary.main },
                              { label: 'Fahrzeuge', value: '18', color: theme.palette.info.main },
                              { label: 'Umsatz', value: '€2.4k', color: theme.palette.warning.main },
                            ].map((kpi, i) => (
                              <Box key={i} sx={{
                                flex: 1,
                                bgcolor: '#fff',
                                borderRadius: 1,
                                p: 1,
                                borderLeft: `3px solid ${kpi.color}`,
                              }}>
                                <Typography sx={{ fontSize: '0.5rem', color: '#999' }}>{kpi.label}</Typography>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#333' }}>{kpi.value}</Typography>
                              </Box>
                            ))}
                          </Box>
                          {/* Map + Table Row */}
                          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                            {/* Mini Map */}
                            <Box sx={{
                              flex: 1.2,
                              bgcolor: '#fff',
                              borderRadius: 1,
                              overflow: 'hidden',
                              position: 'relative',
                              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            }}>
                              <Box sx={{ position: 'absolute', top: 6, left: 8 }}>
                                <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#555' }}>Live-Karte</Typography>
                              </Box>
                              {/* Grid lines */}
                              {[...Array(4)].map((_, i) => (
                                <Box key={`mh-${i}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${25 * (i + 1)}%`, height: 1, bgcolor: alpha('#000', 0.04) }} />
                              ))}
                              {[...Array(5)].map((_, i) => (
                                <Box key={`mv-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${16.6 * (i + 1)}%`, width: 1, bgcolor: alpha('#000', 0.04) }} />
                              ))}
                              {/* Driver dots */}
                              {[
                                { top: '30%', left: '25%' },
                                { top: '55%', left: '60%' },
                                { top: '40%', left: '45%' },
                                { top: '70%', left: '30%' },
                                { top: '25%', left: '75%' },
                              ].map((pos, i) => (
                                <Box key={`dot-${i}`} sx={{
                                  position: 'absolute',
                                  ...pos,
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  bgcolor: theme.palette.primary.main,
                                  border: '2px solid #fff',
                                  boxShadow: 1,
                                }} />
                              ))}
                            </Box>
                            {/* Table */}
                            <Box sx={{
                              flex: 0.8,
                              bgcolor: '#fff',
                              borderRadius: 1,
                              p: 1,
                              display: 'flex',
                              flexDirection: 'column',
                            }}>
                              <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, color: '#555', mb: 0.5 }}>Aktuelle Fahrten</Typography>
                              {[
                                { status: theme.palette.success.main },
                                { status: theme.palette.primary.main },
                                { status: theme.palette.info.main },
                                { status: theme.palette.success.main },
                                { status: theme.palette.warning.main },
                              ].map((row, i) => (
                                <Box key={`row-${i}`} sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  py: 0.4,
                                  borderBottom: '1px solid #f0f0f0',
                                }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: row.status }} />
                                  <Box sx={{ flex: 1, height: 6, bgcolor: '#eee', borderRadius: 0.5 }} />
                                  <Box sx={{ width: 24, height: 6, bgcolor: '#eee', borderRadius: 0.5 }} />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {/* Monitor Stand */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{
                      width: '100%',
                      height: 6,
                      bgcolor: '#1a1a1a',
                      borderRadius: '0 0 4px 4px',
                    }} />
                    <Box sx={{
                      width: 80,
                      height: 40,
                      background: 'linear-gradient(180deg, #2a2a2a, #1a1a1a)',
                    }} />
                    <Box sx={{
                      width: 140,
                      height: 8,
                      bgcolor: '#1a1a1a',
                      borderRadius: '0 0 8px 8px',
                    }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
      
      {/* Mobile App Section - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="mobile-app" sx={{ py: 10, px: 3, bgcolor: theme.palette.common.white }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {/* CSS Smartphone Mockup */}
                  <Box
                    sx={{
                      width: 280,
                      height: 500,
                      borderRadius: '36px',
                      border: '4px solid #1a1a1a',
                      bgcolor: '#1a1a1a',
                      overflow: 'hidden',
                      boxShadow: 10,
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Status Bar */}
                    <Box sx={{
                      height: 44,
                      bgcolor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2.5,
                    }}>
                      <Typography variant="caption" sx={{ color: '#000', fontWeight: 600, fontSize: '0.7rem' }}>9:41</Typography>
                      <Box sx={{ width: 80, height: 24, borderRadius: '12px', bgcolor: '#000' }} />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 10, borderRadius: 1, bgcolor: '#000' }} />
                        <Box sx={{ width: 16, height: 10, borderRadius: 1, bgcolor: '#000' }} />
                      </Box>
                    </Box>
                    {/* App Header */}
                    <Box sx={{
                      height: 56,
                      bgcolor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#000', fontWeight: 700 }}>{brandingConfig.appName}</Typography>
                    </Box>
                    {/* App Content */}
                    <Box sx={{ flex: 1, bgcolor: '#f5f5f5', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Map Area */}
                      <Box sx={{
                        flex: 1,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}>
                        <LocationIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.6 }} />
                        {/* Street grid lines */}
                        {[...Array(5)].map((_, i) => (
                          <Box key={`h-${i}`} sx={{ position: 'absolute', left: 0, right: 0, top: `${20 * (i + 1)}%`, height: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                        ))}
                        {[...Array(4)].map((_, i) => (
                          <Box key={`v-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${25 * (i + 1)}%`, width: 1, bgcolor: alpha(theme.palette.primary.main, 0.08) }} />
                        ))}
                      </Box>
                      {/* Booking Card */}
                      <Box sx={{
                        bgcolor: '#fff',
                        borderRadius: 2,
                        p: 1.5,
                        boxShadow: 2,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'green' }} />
                          <Box sx={{ flex: 1, height: 10, bgcolor: '#eee', borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'red' }} />
                          <Box sx={{ flex: 1, height: 10, bgcolor: '#eee', borderRadius: 1 }} />
                        </Box>
                      </Box>
                      {/* CTA Button */}
                      <Box sx={{
                        bgcolor: theme.palette.primary.main,
                        borderRadius: 2,
                        py: 1.5,
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" sx={{ color: '#000', fontWeight: 700, fontSize: '0.75rem' }}>Taxi bestellen</Typography>
                      </Box>
                    </Box>
                    {/* Bottom Nav */}
                    <Box sx={{
                      height: 50,
                      bgcolor: '#fff',
                      borderTop: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      px: 1,
                    }}>
                      <LocationIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                      <EventIcon sx={{ fontSize: 20, color: '#bbb' }} />
                      <RouteIcon sx={{ fontSize: 20, color: '#bbb' }} />
                      <PeopleIcon sx={{ fontSize: 20, color: '#bbb' }} />
                    </Box>
                    {/* Home Indicator */}
                    <Box sx={{ height: 20, bgcolor: '#1a1a1a', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Box sx={{ width: 100, height: 4, borderRadius: 2, bgcolor: '#555' }} />
                    </Box>
                  </Box>
                  {/* Gradient background behind phone */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -15,
                      right: -15,
                      bottom: -15,
                      left: -15,
                      borderRadius: 5,
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      zIndex: 0,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
                  Mobile App für Kunden und Fahrer
                </Typography>
                <Typography variant="body1" paragraph>
                  Die {brandingConfig.appName}-Plattform beinhaltet leistungsstarke mobile Apps sowohl für Ihre Kunden als auch für Ihre Fahrer. Bieten Sie ein modernes Taxi-Erlebnis, das mit den größten Anbietern mithalten kann.
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 3 }}>
                  {mobileFeatures.map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ mr: 2, color: theme.palette.primary.main }}>
                          {feature.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
      
      {/* White Label Section - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="white-label" sx={{ py: 10, px: 3, bgcolor: theme.palette.grey[50] }}>
          <Container maxWidth="lg">
            <Typography variant="h2" component="h2" align="center" fontWeight="bold" sx={{ mb: 2 }}>
              White-Label Lösung
            </Typography>
            <Typography variant="h5" component="p" align="center" color="textSecondary" sx={{ mb: 6 }}>
              Individuell anpassbar für Ihr Unternehmen
            </Typography>
            
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom>
                  Ihre Marke im Vordergrund
                </Typography>
                <Typography variant="body1" paragraph>
                  NextCab ist vollständig als White-Label-Lösung konzipiert. Passen Sie das Erscheinungsbild an Ihre Unternehmensfarben und Ihr Logo an, um ein konsistentes Markenerlebnis zu bieten.
                </Typography>
                <Typography variant="body1" paragraph>
                  Ihre Kunden nutzen eine App, die aussieht und sich anfühlt, als wäre sie speziell für Ihr Unternehmen entwickelt worden.
                </Typography>
                
                <Box sx={{ mt: 4 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body1">Sicher und zuverlässig</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DevicesIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body1">Optimiert für alle Geräte</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TemplateIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body1">Anpassbare Vorlagen</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body1">Technischer Support</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    borderRadius: 4,
                    boxShadow: 4,
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.9)})`,
                      zIndex: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 2,
                      padding: 4,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h3" component="div" align="center" fontWeight="bold" gutterBottom>
                      Ihr Brand
                    </Typography>
                    <Typography variant="h5" component="div" align="center" gutterBottom>
                      Taxi App
                    </Typography>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        my: 3,
                      }}
                    >
                      <Typography variant="h4" component="div" color="primary" fontWeight="bold">
                        LOGO
                      </Typography>
                    </Box>
                    <Typography variant="body1" align="center">
                      Vollständig anpassbar an Ihre Unternehmensidentität
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
      
      {/* Login Section - unterschiedliche ID für Standard-App und White-Label-App */}
      <Box 
        id={isDefaultApp ? "client-login" : "login-section"} 
        ref={loginSectionRef}
        sx={{ 
          py: 10, 
          px: 3, 
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography variant="h3" component="h2" gutterBottom sx={{ color: 'black' }}>
                    Admin-Portal
                  </Typography>
                  <Typography variant="h5" component="p" paragraph sx={{ color: 'black' }}>
                    Melden Sie sich an, um auf Ihr {brandingConfig.appName} Administrator-Portal zuzugreifen
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'black', mb: 4, maxWidth: 500 }}>
                    Von hier aus verwalten Sie Ihren gesamten Taxibetrieb - Fahrer, Fahrzeuge, Fahrten und Vorbestellungen. Alles in einer übersichtlichen und intuitiven Oberfläche.
                  </Typography>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Zoom in={true}>
                <LoginContainer elevation={3}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', width: '100%', marginBottom: 3 }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        // Responsives Design für verschiedene Bildschirmgrößen
                        ...(isMobile ? {
                          '& > img:first-of-type': { height: '120px !important' },
                          '& > img:nth-of-type(2)': { height: '180px !important', marginTop: '-90px !important' }
                        } : {})
                      }}>
                        <IconLogo
                          alt={`${brandingConfig.appName} Logo`}
                          style={{ height: 160, marginBottom: 0 }}
                        />
                        <PrimaryLogo
                          alt={brandingConfig.appName}
                          style={{ height: 250, marginTop: -120 }}
                        />
                      </Box>
                      <Typography variant="subtitle1" color="black" sx={{ 
                        mt: -13, 
                        fontWeight: 300, 
                        letterSpacing: 1,
                        ...(isMobile ? { mt: -10 } : {})
                      }}>
                        {brandingConfig.slogan}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="textSecondary" align="left" sx={{ alignSelf: 'flex-start', ml: 1 }}>
                      Login Admin-Portal
                    </Typography>
                  </Box>
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      <AlertTitle>Fehler</AlertTitle>
                      {error}
                    </Alert>
                  )}
                  
                  <Form onSubmit={handleSubmit}>
                    <TextField
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="E-Mail-Adresse"
                      name="email"
                      autoComplete="email"
                      autoFocus={directLogin || !isDefaultApp}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!emailError}
                      helperText={emailError}
                      onBlur={() => validateEmail(email)}
                    />
                    <TextField
                      variant="outlined"
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Passwort"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!passwordError}
                      helperText={passwordError}
                      onBlur={() => validatePassword(password)}
                    />
                    
                    <Link 
                      component={RouterLink}
                      to="/passwort-vergessen"
                      variant="body2"
                      sx={{ display: 'block', textAlign: 'right', mt: 1 }}
                    >
                      Passwort vergessen?
                    </Link>
                    
                    <SubmitButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="secondary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Anmelden'
                      )}
                    </SubmitButton>
                  </Form>

                  {/* Links zu rechtlichen Seiten */}
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid item>
                        <Link 
                          component={RouterLink} 
                          to="/datenschutz" 
                          variant="body2"
                          color="textSecondary"
                        >
                          Datenschutz
                        </Link>
                      </Grid>
                      <Grid item>
                        <Link 
                          component={RouterLink} 
                          to="/impressum" 
                          variant="body2"
                          color="textSecondary"
                        >
                          Impressum
                        </Link>
                      </Grid>
                    </Grid>
                  </Box>
                </LoginContainer>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Footer - nur für Default App */}
      {(!directLogin && isDefaultApp) && (
        <Box id="contact" sx={{ bgcolor: theme.palette.secondary.main, color: 'white', py: 6, px: 3 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconLogo
                    alt={`${brandingConfig.appName} Logo`}
                    style={{ height: 44, marginRight: 0 }}
                  />
                  <PrimaryLogo
                    alt={brandingConfig.appName}
                    style={{ height: 130, marginLeft: -4 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Die komplette Lösung für moderne Taxi-Unternehmen
                </Typography>
                <Typography variant="body2">
                  {brandingConfig.copyright}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" gutterBottom>
                  Navigation
                </Typography>
                <Box component="nav">
                  {navItems.map((item) => (
                    <FooterLink
                      key={item.text}
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        smoothScrollToElement(item.href.substring(1));
                      }}
                    >
                      {item.text}
                    </FooterLink>
                  ))}
                  <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
                  <FooterLink component={RouterLink} to="/datenschutz">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PrivacyTipIcon fontSize="small" sx={{ mr: 1 }} />
                      Datenschutzerklärung
                    </Box>
                  </FooterLink>
                  <FooterLink component={RouterLink} to="/impressum">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <GavelIcon fontSize="small" sx={{ mr: 1 }} />
                      Impressum
                    </Box>
                  </FooterLink>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" gutterBottom>
                  Kontakt
                </Typography>
                <Typography variant="body2" paragraph>
                  Haben Sie Fragen oder möchten mehr erfahren?
                </Typography>
                <Button 
                  variant="outlined" 
                  color="inherit"
                  startIcon={<PhoneIcon />}
                  href={`tel:${brandingConfig.phone.replace(/\s/g, '')}`}
                  sx={{ mb: 2 }}
                >
                  {brandingConfig.phone}
                </Button>
                <br />
                <Button 
                  variant="outlined" 
                  color="inherit"
                  href={`mailto:${brandingConfig.email}`}
                >
                  {brandingConfig.email}
                </Button>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
      
      {/* Vereinfachter Footer für White-Label Apps */}
      {(!directLogin && !isDefaultApp) && (
        <Box sx={{ bgcolor: theme.palette.secondary.main, color: 'white', py: 3, px: 3 }}>
          <Container maxWidth="lg">
            <Grid container spacing={2} justifyContent="space-between" alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IconLogo
                    alt={`${brandingConfig.appName} Logo`}
                    style={{ height: 36, marginRight: 0 }}
                  />
                  <PrimaryLogo
                    alt={brandingConfig.appName}
                    style={{ height: 100, marginLeft: -4 }}
                  />
                </Box>
                <Typography variant="body2">
                  {brandingConfig.copyright}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {brandingConfig.phone} | {brandingConfig.email}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {brandingConfig.address}
                </Typography>
                <Grid container spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Grid item>
                    <FooterLink component={RouterLink} to="/datenschutz">
                      Datenschutzerklärung
                    </FooterLink>
                  </Grid>
                  <Grid item>
                    <FooterLink component={RouterLink} to="/impressum">
                      Impressum
                    </FooterLink>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
    </>
  );
};

export default LandingPage;